// TUT-450 — os laudos: lista, revisores, fila entre processos, prazo e transições.
import { demoApi } from "../demo-data";
import type {
  AssignableMembersResponse,
  ExpertReport,
  ExpertReportListResponse,
  ExpertReportQueueQuery,
  ExpertReportQueueResponse,
  ExpertReportReviewersResponse,
  ExpertReportStatusKey,
} from "../types";
import { request } from "./infra";

// Lido aqui, e não importado: o empacotador só dobra a constante no módulo
// que a escreve, e é isso que tira o demo do build de produção (TUT-448).
const useDemoApi = import.meta.env.VITE_API_MODE === "demo";

export const apiDosLaudos = {
  expertReports(expertiseId: string): Promise<ExpertReportListResponse> {
    if (useDemoApi) return demoApi.expertReports(expertiseId);
    return request<ExpertReportListResponse>(
      `/expertises/${encodeURIComponent(expertiseId)}/reports`,
    );
  },
  /**
   * TUT-498b — quem pode ELABORAR laudo (`laudos.escrever`, só ativos e de
   * dentro): a lista do lote da Elaboração. O servidor confere o pedido com
   * o mesmo predicado.
   */
  expertReportAssignableMembers(): Promise<AssignableMembersResponse> {
    if (useDemoApi) return demoApi.expertReportAssignableMembers();
    return request<AssignableMembersResponse>("/expert-reports/assignable-members");
  },
  expertReportReviewers(): Promise<ExpertReportReviewersResponse> {
    if (useDemoApi) return demoApi.expertReportReviewers();
    return request<ExpertReportReviewersResponse>("/expert-report-reviewers");
  },
  // TUT-242 — a fila de laudos entre processos (aba "Laudos" da Elaboração).
  // Espelha `legalBriefsAcrossProcesses`: mesmo `mine`, mesmo teto de
  // `pageSize` (a API tem `@Max(100)`).
  expertReportsAcrossProcesses(
    query: ExpertReportQueueQuery = {},
  ): Promise<ExpertReportQueueResponse> {
    if (useDemoApi) return demoApi.expertReportsAcrossProcesses(query);
    const params = new URLSearchParams({
      page: String(query.page ?? 1),
      pageSize: String(query.pageSize ?? 20),
    });
    for (const status of query.status ?? []) params.append("status", status);
    if (query.mine) params.set("mine", "true");
    // TUT-480 — o recorte do laudo: elaborador, revisor e data de protocolo.
    if (query.assignedUserId) params.set("assignedUserId", query.assignedUserId);
    if (query.assignedReviewerUserId) {
      params.set("assignedReviewerUserId", query.assignedReviewerUserId);
    }
    if (query.periodoDe) params.set("periodoDe", query.periodoDe);
    if (query.periodoAte) params.set("periodoAte", query.periodoAte);
    if (query.clientOrganizationId) {
      params.set("clientOrganizationId", query.clientOrganizationId);
    }
    if (query.defendantOrganizationId) {
      params.set("defendantOrganizationId", query.defendantOrganizationId);
    }
    if (query.requiresFilingProof) params.set("requiresFilingProof", "true");
    if (query.agendamento) params.set("agendamento", "true");
    return request<ExpertReportQueueResponse>(`/expert-reports?${params}`);
  },
  assignExpertReportReviewer(
    reportId: string,
    assignedReviewerUserId: string | null,
  ): Promise<ExpertReport> {
    if (useDemoApi)
      return demoApi.assignExpertReportReviewer(
        reportId,
        assignedReviewerUserId,
      );
    return request<ExpertReport>(
      `/expert-reports/${encodeURIComponent(reportId)}/reviewer-assignment`,
      {
        method: "PUT",
        body: JSON.stringify({ assignedReviewerUserId }),
      },
    );
  },
  /**
   * TUT-260 — grava o prazo manual do laudo. `null` devolve o laudo à herança
   * da perícia, e por isso o corpo sempre leva a chave: ausência e nulo
   * significam coisas diferentes aqui.
   */
  setExpertReportDue(
    reportId: string,
    dueAt: string | null,
  ): Promise<ExpertReport> {
    if (useDemoApi) return demoApi.setExpertReportDue(reportId, dueAt);
    return request<ExpertReport>(
      `/expert-reports/${encodeURIComponent(reportId)}/due`,
      { method: "PUT", body: JSON.stringify({ dueAt }) },
    );
  },
  openExpertReport(
    expertiseId: string,
    input: { assignedUserId?: string } = {},
  ): Promise<ExpertReport> {
    if (useDemoApi) return demoApi.openExpertReport(expertiseId);
    return request<ExpertReport>(
      `/expertises/${encodeURIComponent(expertiseId)}/reports`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
  transitionExpertReport(
    reportId: string,
    input: { to: ExpertReportStatusKey; reason?: string | null },
  ): Promise<ExpertReport> {
    if (useDemoApi) return demoApi.transitionExpertReport(reportId, input);
    return request<ExpertReport>(
      `/expert-reports/${encodeURIComponent(reportId)}/transitions`,
      {
        method: "POST",
        body: JSON.stringify({
          to: input.to.toUpperCase(),
          ...(input.reason ? { reason: input.reason } : {}),
        }),
      },
    );
  },
  /**
   * TUT-524 (D18) — "eu pego": o laudo passa a quem pede, se ninguém pegou
   * antes. Quem chega depois recebe a recusa "Já está com {nome}".
   */
  claimExpertReport(reportId: string): Promise<ExpertReport> {
    if (useDemoApi) return demoApi.claimExpertReport(reportId);
    return request<ExpertReport>(`/expert-reports/${encodeURIComponent(reportId)}/claim`, { method: "POST" });
  },
  reopenExpertReport(
    reportId: string,
    input: { reason: string },
  ): Promise<ExpertReport> {
    if (useDemoApi) return demoApi.reopenExpertReport(reportId, input);
    return request<ExpertReport>(
      `/expert-reports/${encodeURIComponent(reportId)}/reopen`,
      { method: "POST", body: JSON.stringify(input) },
    );
  },
};
